import React, { createContext, useContext } from 'react';
import defaultTranslations from './defaultTranslations';

type TranslationContextType = {
  translations: typeof defaultTranslations;
};


const TranslationContext = createContext<TranslationContextType>({
  translations: defaultTranslations,
});

export const TranslationProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const contextValue: TranslationContextType = {
    translations: defaultTranslations,
  };
  
  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationContext = () => useContext(TranslationContext);

export const useTranslations = (language: string = 'en_GB') => {
  const { translations } = useTranslationContext();
  
  // Check if translations for requested language exist
  const hasRequestedLanguage = !!translations[language as keyof typeof translations];
  
  // If no translations for requested language exist, use English
  const resolvedLanguage = hasRequestedLanguage ? language : 'en_GB';
  
  // Get translations for selected language
  const selectedTranslations = translations[resolvedLanguage as keyof typeof translations];
  
  // Return proxy function that checks if translation exists
  return new Proxy(selectedTranslations, {
    get: (target, prop) => {
      if (typeof prop !== 'string') {
        // @ts-expect-error dynamic access
        return target[prop as keyof typeof target];
      }

      const value = target[prop as keyof typeof target];

      if (value !== null && value !== undefined && value !== '') {
        return value;
      }

      const langFallback = defaultTranslations[resolvedLanguage as keyof typeof defaultTranslations]?.[prop as keyof typeof target];
      if (langFallback !== null && langFallback !== undefined && langFallback !== '') {
        return langFallback;
      }

      const enFallback = defaultTranslations.en_GB?.[prop as keyof typeof defaultTranslations.en_GB];
      if (enFallback !== null && enFallback !== undefined && enFallback !== '') {
        return enFallback;
      }

      return '';
    }
  });
};

